/**
 * CSS class added to elements once they have been successfully patched.
 */
export const PATCHED_CLASS = 'mega-menu-patched';

/**
 * Interval to refresh Megamenu Preferred Names
 */
export const MEGAMENU_NAME_REFRESH_INTERVAL = 2000;

/**
 * Breakpoints for the application layout.
 */
export const MEGA_MENU_MOBILE_BREAKPOINT = 940;

/**
 * Remove prefixes when initials are created
 */
export const REMOVE_PREFIXES_FOR_INITIALS = true;

const PREFIXES_RE = /^(Ms\.|Mr\.|Mrs\.|Dr\.)\s*/i;

/**
 * Generates initials from firstName and lastName, optionally removing prefixes.
 */
export function getInitials(firstName: string, lastName: string): string {
  let firstNameCleaned = firstName.trim();

  if (REMOVE_PREFIXES_FOR_INITIALS) {
    firstNameCleaned = firstNameCleaned.replace(PREFIXES_RE, '');
  }

  const firstNameInitial = firstNameCleaned[0]?.toUpperCase() ?? '';
  const lastNameInitial = lastName.trim()[0]?.toUpperCase() ?? '';

  return firstNameInitial + lastNameInitial;
}

/**
 * Patches only the name text nodes inside the widget's DOM elements.
 * Uses direct Text node manipulation (not innerHTML) so it does not trigger
 * MutationObservers. The widget's permissions data, Act-As list, and MLS ID
 * are completely untouched.
 *
 * On small screens the widget shows initials — we mirror that.
 *
 * Returns true if the patch was applied, false if the element wasn't ready.
 */
export function applyDisplayNamePatch(
  firstName: string,
  lastName: string,
  isMobile: boolean,
): boolean {
  const fullName = `${firstName} ${lastName}`;
  const initials = getInitials(firstName, lastName);
  const displayName = isMobile ? initials : fullName;
  let patched = false;

  // Main nav button — name="usernameloggedin"
  for (const el of Array.from(document.getElementsByName('usernameloggedin'))) {
    const idSpan = el.querySelector('.idUserNameLoggedIn');
    if (!idSpan) {
      // Skeleton phase — keep it hidden (via CSS opacity: 0 by default)
      continue;
    }

    el.setAttribute('title', fullName.toUpperCase());

    for (const node of Array.from(el.childNodes)) {
      if (node.nodeType === Node.TEXT_NODE) {
        node.textContent = displayName + ' ';
        break;
      }
    }

    // Reveal now that the correct name is in place.
    (el as HTMLElement).classList.add(PATCHED_CLASS);
    patched = true;
  }

  // Secondary name-only element — name="usernameloggedinusername"
  for (const el of Array.from(
    document.getElementsByName('usernameloggedinusername'),
  )) {
    el.textContent = fullName; // always full name here regardless of width
    (el as HTMLElement).classList.add(PATCHED_CLASS);
  }

  // Initials avatar — name="usernameinitials"
  // The widget writes "[IN]" as a placeholder on first render then replaces
  // it. We handle both states.
  for (const el of Array.from(document.getElementsByName('usernameinitials'))) {
    el.textContent = el.textContent?.includes('[IN]')
      ? el.textContent.replace('[IN]', initials)
      : initials;
    (el as HTMLElement).classList.add(PATCHED_CLASS);
  }

  // name="usernameloggedinuserid" intentionally left alone.
  return patched;
}
